/**
 * Discussion Forum Feature - Main Export
 *
 * Phase 1.1 Update:
 * - Added Firestore integration via discussionFirestoreService
 * - forumService now delegates to Firestore for message persistence
 * - Supports effectiveUserId pattern for shared trips
 *
 * Phase 1.2 Update:
 * - Added real-time hooks: useDiscussionMessages and usePostMessage
 * - useDiscussionMessages provides onSnapshot-based live message updates
 * - usePostMessage provides convenient message posting interface
 */

import { forumService } from './services/forumService.js';
import * as discussionFirestoreService from './services/discussionFirestoreService.js';

// Import components directly (not via barrel export)
import DiscussionOverlay from './components/DiscussionOverlay/DiscussionOverlay.jsx';
import ChatToggle from './components/ChatToggle/ChatToggle.jsx';
import MessageList from './components/MessageList/MessageList.jsx';
import MessageBubble from './components/MessageBubble/MessageBubble.jsx';
import InputArea from './components/InputArea/InputArea.jsx';
import ChatHeader from './components/DiscussionOverlay/ChatHeader.jsx';
import ForumMain from './components/ForumMain/ForumMain.jsx';

// Import hooks directly
import { useDiscussionMessages, usePostMessage } from './hooks/index.js';

export const initDiscussionForumServices = () => {
  forumService.init();
  console.log('Discussion Forum services initialized');
};

export const discussionForum = {
  // Service instance
  service: forumService,

  // Convenience methods
  async createTripForum(tripId, ownerId) {
    return forumService.createTripForum(tripId, ownerId);
  },

  async createThread(tripId, userId, threadData) {
    return forumService.createThread(tripId, userId, threadData);
  },

  async postMessage(tripId, threadId, userId, messageData) {
    return forumService.postMessage(tripId, threadId, userId, messageData);
  },

  async addUserToForum(tripId, userId, role = 'collaborator', invitedBy) {
    return forumService.addUserToForum(tripId, userId, role, invitedBy);
  },

  async getForumThreads(tripId, options = {}) {
    return forumService.getForumThreads(tripId, options);
  },

  async getThreadMessages(tripId, threadId, options = {}) {
    return forumService.getThreadMessages(tripId, threadId, options);
  },

  hasPermission(userId, tripId, permission) {
    return forumService.hasPermission(userId, tripId, permission);
  },

  getForumStatistics(tripId) {
    return forumService.getForumStatistics(tripId);
  }
};

// Export components (Phase 1.3)
export {
  DiscussionOverlay,
  ChatToggle,
  MessageList,
  MessageBubble,
  InputArea,
  ChatHeader,
  ForumMain
};

// Export services
export { forumService };
export { discussionFirestoreService };

// Export hooks (Phase 1.2)
export { useDiscussionMessages, usePostMessage };

export default discussionForum;